import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  ColorPicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  TimePicker,
  Typography,
  message,
} from 'antd';
import {
  ClockCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import MainLayout from '../../layout/layout';
import { apiClient } from '../../../config/apiClient';

const { Text } = Typography;

const COLOR_PRESETS = [
  '#52c41a',
  '#faad14',
  '#722ed1',
  '#1890ff',
  '#f5222d',
  '#eb2f96',
  '#13c2c2',
  '#fa8c16',
  '#d8ff3e',
  '#2f54eb',
];

const toHex = (color) => {
  if (!color) return '#1890ff';
  if (typeof color === 'string') return color;
  if (typeof color.toHexString === 'function') return color.toHexString();
  return '#1890ff';
};

const descripcionHorario = (horaDesde, horaHasta) => {
  if (horaDesde && horaHasta) return `De ${horaDesde} a ${horaHasta}`;
  if (!horaDesde && horaHasta) return `Hasta las ${horaHasta}`;
  if (horaDesde && !horaHasta) return `Después de ${horaDesde}`;
  return 'Sin horario';
};

const FranjasHorarias = () => {
  const [franjas, setFranjas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingFranja, setEditingFranja] = useState(null);
  const [form] = Form.useForm();

  const loadFranjas = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/torneos/config/franjas-horarias');
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        message.error(err.message || 'No se pudieron cargar las franjas horarias');
        return;
      }
      const data = await response.json();
      setFranjas(Array.isArray(data?.franjas) ? data.franjas : []);
    } catch (error) {
      console.error('Error al cargar franjas horarias:', error);
      message.error('Error al cargar las franjas horarias');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFranjas();
  }, [loadFranjas]);

  const handleOpenModal = (franja = null) => {
    setEditingFranja(franja);
    if (franja) {
      form.setFieldsValue({
        nombre: franja.nombre,
        horaDesde: franja.horaDesde ? dayjs(franja.horaDesde, 'HH:mm') : null,
        horaHasta: franja.horaHasta ? dayjs(franja.horaHasta, 'HH:mm') : null,
        color: franja.color,
        orden: franja.orden,
        activo: franja.activo !== false,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        color: COLOR_PRESETS[franjas.length % COLOR_PRESETS.length],
        orden: (franjas.reduce((max, item) => Math.max(max, Number(item.orden) || 0), 0) || 0) + 1,
        activo: true,
      });
    }
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const horaDesde = values.horaDesde ? values.horaDesde.format('HH:mm') : null;
      const horaHasta = values.horaHasta ? values.horaHasta.format('HH:mm') : null;
      if (!horaDesde && !horaHasta) {
        message.warning('Indicá al menos una hora: desde y/o hasta');
        return;
      }

      const payload = {
        nombre: values.nombre,
        horaDesde,
        horaHasta,
        color: toHex(values.color),
        orden: values.orden,
        activo: values.activo !== false,
      };

      setSaving(true);
      const response = editingFranja
        ? await apiClient.put(`/torneos/config/franjas-horarias/${editingFranja.id}`, payload)
        : await apiClient.post('/torneos/config/franjas-horarias', payload);

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        message.error(err.message || 'No se pudo guardar la franja horaria');
        return;
      }

      message.success(editingFranja ? 'Franja horaria actualizada' : 'Franja horaria creada');
      setModalVisible(false);
      setEditingFranja(null);
      await loadFranjas();
    } catch (error) {
      if (error?.errorFields) return;
      console.error('Error al guardar franja horaria:', error);
      message.error('Error al guardar la franja horaria');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (franja) => {
    try {
      const response = await apiClient.delete(`/torneos/config/franjas-horarias/${franja.id}`);
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        message.error(err.message || 'No se pudo eliminar la franja horaria');
        return;
      }
      message.success('Franja horaria eliminada');
      loadFranjas();
    } catch (error) {
      console.error('Error al eliminar franja horaria:', error);
      message.error('Error al eliminar la franja horaria');
    }
  };

  const columns = [
    {
      title: 'Color',
      dataIndex: 'color',
      width: 80,
      render: (color) => (
        <span
          style={{
            display: 'inline-block',
            width: 28,
            height: 28,
            borderRadius: 8,
            background: toHex(color),
            border: '1px solid #444',
          }}
        />
      ),
    },
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      render: (nombre, record) => (
        <Space>
          <Text strong>{nombre}</Text>
          {record.activo === false ? <Text type="secondary">(inactiva)</Text> : null}
        </Space>
      ),
    },
    {
      title: 'Horario',
      key: 'horario',
      render: (_, record) => descripcionHorario(record.horaDesde, record.horaHasta),
    },
    {
      title: 'Orden',
      dataIndex: 'orden',
      width: 90,
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 140,
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => handleOpenModal(record)} />
          <Popconfirm
            title="¿Eliminar esta franja horaria?"
            okText="Eliminar"
            cancelText="Cancelar"
            onConfirm={() => handleDelete(record)}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <Card
        title={(
          <Space>
            <ClockCircleOutlined />
            <span>Franja horaria</span>
          </Space>
        )}
        extra={(
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadFranjas} loading={loading}>
              Actualizar
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal()}>
              Nueva franja
            </Button>
          </Space>
        )}
        loading={loading}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Cargá las franjas horarias que vas a usar en la gestión del torneo. Podés dejar solo hora hasta (Hasta las 16), ambas (De 16 a 19) o solo hora desde (Después de 19)."
        />

        <Table
          rowKey="id"
          columns={columns}
          dataSource={franjas}
          pagination={false}
          locale={{ emptyText: 'Todavía no hay franjas horarias' }}
        />
      </Card>

      <Modal
        title={editingFranja ? 'Editar franja horaria' : 'Nueva franja horaria'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        okText="Guardar"
        cancelText="Cancelar"
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="nombre"
            label="Nombre"
            rules={[{ required: true, message: 'Ingresá el nombre' }]}
          >
            <Input placeholder="Ej: Franja horaria 1" maxLength={100} />
          </Form.Item>

          <Form.Item
            name="horaDesde"
            label="Hora desde"
            extra="Dejala vacía si la franja es “hasta las …”."
          >
            <TimePicker format="HH:mm" style={{ width: '100%' }} placeholder="Sin hora desde" />
          </Form.Item>

          <Form.Item
            name="horaHasta"
            label="Hora hasta"
            extra="Dejala vacía si la franja es “después de …”."
          >
            <TimePicker format="HH:mm" style={{ width: '100%' }} placeholder="Sin hora hasta" />
          </Form.Item>

          <Form.Item
            name="color"
            label="Color"
            rules={[{ required: true, message: 'Elegí un color' }]}
            getValueFromEvent={(color) => toHex(color)}
          >
            <ColorPicker
              showText
              format="hex"
              defaultFormat="hex"
              disabledAlpha
              presets={[{ label: 'Colores recomendados', colors: COLOR_PRESETS }]}
            />
          </Form.Item>

          <Form.Item name="orden" label="Orden">
            <InputNumber min={1} max={999} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="activo" label="Activa" valuePropName="checked">
            <Switch checkedChildren="Sí" unCheckedChildren="No" />
          </Form.Item>
        </Form>
      </Modal>
    </MainLayout>
  );
};

export default FranjasHorarias;
